// Only ever return to an internal path. Anything absolute or protocol-relative
// (`//evil.com`, `/\evil.com`) arriving via ?redirect= is discarded so the
// param can't be used as an open redirect.
export function safeInternalPath(path: string | null | undefined): string | null {
  if (!path || !path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) {
    return null;
  }
  return path;
}

const RETURN_URL_KEY = "postOnboardingReturnUrl";
const RETURN_URL_TTL_MS = 5 * 60 * 1000;

/**
 * Where to send someone once they finish signing in, when it isn't the
 * dashboard. It belongs to the sign-in they are in the middle of: abandon that
 * and start again from somewhere else and they should land in the app, not back
 * on the page whose CTA they gave up on. So it lives in sessionStorage (dies
 * with the tab) and expires five minutes after it was stashed.
 */
export function stashReturnUrl(path: string | null | undefined) {
  const dest = safeInternalPath(path);
  if (!dest) return;
  try {
    window.sessionStorage.setItem(RETURN_URL_KEY, JSON.stringify({ dest, at: Date.now() }));
  } catch {}
}

/** Reads the stashed destination and clears it. Null once it has gone stale. */
export function takeReturnUrl(): string | null {
  try {
    const raw = window.sessionStorage.getItem(RETURN_URL_KEY);
    window.sessionStorage.removeItem(RETURN_URL_KEY);
    if (!raw) return null;
    const { dest, at } = JSON.parse(raw) as { dest?: string; at?: number };
    if (!at || Date.now() - at > RETURN_URL_TTL_MS) return null;
    return safeInternalPath(dest);
  } catch {
    return null;
  }
}
